import React from 'react';
import moment, { DurationInputArg1, DurationInputArg2, Moment } from 'moment';
import Polyglot from 'node-polyglot';
import { Tab } from 'semantic-ui-react';
import { TIME_FRAME_TYPES, TIME_RANGE_TYPES } from './Constants';
import { DATETIME_FORMAT } from '../constants';
import { TimeRangeQuick } from './TimeRangeQuick';
import { TimeRangeAbsolute } from './TimeRangeAbsolute';
// import TimeRangeQuick from "./components/timeRangeQuick";
// import TimeRangeAbsolute from "./components/timeRangeAbsolute";
// import TimeRangeSelectionPanel from "./components/timeRangeSelectionPanel";
// import UiIcon from '../uiIcon';
import polyglotUIComponents from '../i18n';
import { MODE_UI_OPTIONS } from '../modeUI';
import './index.css';
import { getTimeFrames, TimeFrameOption } from './Util';
import UiModal from '../uiModal';
import UiButton from '../uiButton';

export type TimeFrameSelectionProps = {
  onChange?: (dateTimeIniAbsolute: Moment, dateTimeEndAbsolute: Moment) => void;
  timeFrame?: TIME_FRAME_TYPES;
  nameButtonSend?: string;
  mode?: MODE_UI_OPTIONS;
  language?: string;
};

export type TimeFrameSelectionState = {
  timeFrame: TIME_FRAME_TYPES;
  timeRange: TIME_RANGE_TYPES;
  openMenu: boolean;
  dateTimeIniAbsolute: moment.Moment;
  dateTimeEndAbsolute: moment.Moment;
};

/**
 * Time frame selection
 * Stateful Component to select relative or absolute Date Range
 */
export class TimeFrameSelection extends React.PureComponent<
  TimeFrameSelectionProps,
  TimeFrameSelectionState
> {
  polyglot: Polyglot;
  timeFrames: TimeFrameOption[];

  constructor(props: Readonly<TimeFrameSelectionProps>) {
    super(props);
    this.polyglot = polyglotUIComponents(props.language);
    this.timeFrames = getTimeFrames(this.polyglot);
    const { dateTimeIniAbsolute, dateTimeEndAbsolute } = this.dateTimeMinMax(
      props.timeFrame || TIME_FRAME_TYPES.LAST_15_MINUTES
    );
    this.state = {
      timeFrame: props.timeFrame || TIME_FRAME_TYPES.LAST_15_MINUTES,
      timeRange: TIME_RANGE_TYPES.QUICK,
      openMenu: false,
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
    };
  }

  /**
   * Date time min max
   * this function returns the date time ini and the data time max (moment object) for a time frame
   * @param number timeFrame
   * @return object{moment, moment} : {dateTimeIni, dateTimeEnd}
   */
  dateTimeMinMax = (timeFrame: TIME_FRAME_TYPES) => {
    const dateTimeEndAbsolute = moment();
    const dateTimeIniAbsolute = moment().subtract(
      this.timeFrames[timeFrame].substract,
      this.timeFrames[timeFrame].unit
    );

    return {
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
    };
  };

  /**
   * Select time frame quick
   * @param int timeFrame
   */
  selectTimeFrameQuick = (timeFrame: TIME_FRAME_TYPES) => {
    const { onChange } = this.props;
    const { dateTimeIniAbsolute, dateTimeEndAbsolute } =
      this.dateTimeMinMax(timeFrame);
    this.setState({
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
      timeFrame,
    });
    onChange && onChange(dateTimeIniAbsolute, dateTimeEndAbsolute);
    this.handleClose();
  };

  /**
   * Select time frame absolute
   * @param moment dateTimeIniAbsolute
   * @param moment dateTimeEndAbsolute
   */
  selectTimeFrameAbsolute = (
    dateTimeIniAbsolute: Moment,
    dateTimeEndAbsolute: Moment
  ) => {
    const { onChange } = this.props;
    this.setState({
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
    });
    onChange && onChange(dateTimeIniAbsolute, dateTimeEndAbsolute);
    this.handleClose();
  };

  /**
   * Handle open
   */
  handleOpen = () => {
    this.setState({ openMenu: true });
  };

  /**
   * Handle close
   */
  handleClose = () => {
    this.setState({ openMenu: false });
  };

  /**
   * Toggle time range
   * @param int timeRange
   */
  toggleTimeRange = (timeRange: TIME_RANGE_TYPES) => {
    if (timeRange === TIME_RANGE_TYPES.ABSOLUTE) {
      /* per default show 1 week  */
      const { dateTimeIniAbsolute, dateTimeEndAbsolute } = this.dateTimeMinMax(
        TIME_FRAME_TYPES.LAST_WEEK
      );

      this.setState({
        dateTimeIniAbsolute,
        dateTimeEndAbsolute,
        timeRange,
      });
    } else {
      // TIME_RANGE_TYPES.Quick
      this.setState({
        timeRange,
      });
    }
  };

  render() {
    const {
      timeFrame,
      openMenu,
      timeRange,
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
    } = this.state;
    const {
      nameButtonSend = this.polyglot.t('button.send'),
      mode,
      language,
    } = this.props;

    const timeFrameSelected =
      timeRange === TIME_RANGE_TYPES.QUICK
        ? this.timeFrames[timeFrame].name
        : `${this.polyglot.t('general.from')}: ${dateTimeIniAbsolute.format(
            DATETIME_FORMAT
          )} ${this.polyglot.t('general.to')}: ${dateTimeEndAbsolute.format(
            DATETIME_FORMAT
          )}`;

    return (
      <UiModal
        onClose={this.handleClose}
        onOpen={this.handleOpen}
        closeIcon
        header={{
          icon: 'clock outline',
          content: this.polyglot.t('timeframe_selection_panel.time_range'),
        }}
        trigger={
          <UiButton
            id="TimeFrameSelection_UiButton_select"
            iconName="clock outline"
            label={timeFrameSelected}
            showLabels
            mode={mode}
          />
        }
        size={'small'}
        open={openMenu}
        mode={mode}
      >
        <Tab
          panes={[
            {
              menuItem: this.polyglot.t('timeframe_selection_panel.quick'),
              render: () => (
                <Tab.Pane>
                  <TimeRangeQuick
                    timeFrames={this.timeFrames}
                    selectTimeFrame={this.selectTimeFrameQuick}
                  />
                </Tab.Pane>
              ),
            },
            {
              menuItem: this.polyglot.t('timeframe_selection_panel.absolute'),
              render: () => (
                <Tab.Pane>
                  <TimeRangeAbsolute
                    nameButtonSend={nameButtonSend}
                    dateTimeIniAbsolute={dateTimeIniAbsolute}
                    dateTimeEndAbsolute={dateTimeEndAbsolute}
                    selectTimeFrame={this.selectTimeFrameAbsolute}
                    mode={mode}
                    language={language}
                  />
                </Tab.Pane>
              ),
            },
          ]}
        />
      </UiModal>
    );
  }
}
